import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Link as LinkIcon, Shield, AlertTriangle, CheckCircle, User, Key, RefreshCw } from 'lucide-react';

export default function LinkRoblox() {
  const [cookie, setCookie] = useState('');
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [linkMethod, setLinkMethod] = useState('username'); // 'username' or 'cookie'
  const { user, token, api, linkRoblox, unlinkRoblox, refreshUser } = useAuth();
  const navigate = useNavigate();

  const [refreshingBalance, setRefreshingBalance] = useState(false);

  const handleRefreshBalance = async () => {
    setRefreshingBalance(true);
    try {
      await api.post('/roblox/refresh-balance');
      toast.success('Balance refreshed!');
      refreshUser?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to refresh balance');
    } finally {
      setRefreshingBalance(false);
    }
  };

  const handleLinkByUsername = async (e) => {
    e.preventDefault();
    if (!username.trim()) return;

    if (!token) {
      toast.error('Please log in first');
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post(`/roblox/link-by-username?username=${encodeURIComponent(username)}`);
      toast.success(`Linked to ${res.data.roblox_username}!`);
      refreshUser?.();
      navigate('/app/profile');
    } catch (error) {
      console.error('Link by username error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        navigate('/login');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to link Roblox account');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLinkByCookie = async (e) => {
    e.preventDefault();
    
    if (!token) {
      toast.error('Please log in first');
      navigate('/login');
      return;
    }
    
    // Clean up the cookie - remove any .ROBLOSECURITY= prefix if present
    let cleanCookie = cookie.trim();
    if (cleanCookie.startsWith('.ROBLOSECURITY=')) {
      cleanCookie = cleanCookie.substring(15);
    }
    
    // Validate cookie format - should start with _|WARNING
    if (!cleanCookie.startsWith('_|WARNING')) {
      toast.error('Invalid cookie format. Cookie should start with "_|WARNING:-DO-NOT-SHARE-THIS..."');
      return;
    }

    setLoading(true);
    try {
      const result = await linkRoblox(cleanCookie);
      toast.success(`Linked to ${result.roblox_username}!`);
      navigate('/app/profile');
    } catch (error) {
      console.error('Link by cookie error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        navigate('/login');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to link Roblox account. Make sure your cookie is valid and not expired.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUnlink = async () => {
    setLoading(true);
    try {
      await unlinkRoblox();
      toast.success('Roblox account unlinked');
    } catch (error) {
      toast.error('Failed to unlink account');
    } finally {
      setLoading(false);
    }
  };

  if (user?.roblox_username) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="glass rounded-2xl p-8">
          <div className="flex items-center gap-4 mb-6">
            {user.roblox_avatar_url ? (
              <img
                src={user.roblox_avatar_url}
                alt="Roblox Avatar"
                className="w-20 h-20 rounded-full border-4 border-green-500/30"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-slate-700 flex items-center justify-center text-2xl font-bold">
                {user.roblox_username?.charAt(0)}
              </div>
            )}
            <div>
              <div className="flex items-center gap-2">
                <CheckCircle className="text-green-400" size={20} />
                <span className="text-green-400 font-medium">Linked</span>
              </div>
              <h2 className="font-display text-2xl font-bold text-white">{user.roblox_username}</h2>
            </div>
          </div>

          {/* Roblox Balance Display */}
          <div className="p-4 rounded-lg bg-slate-800/50 border border-slate-700 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-400 text-sm">Roblox Balance</p>
                <p className="text-2xl font-bold text-green-400 font-mono">
                  R$ {user.roblox_balance?.toLocaleString() || 0}
                </p>
              </div>
              {user.roblox_cookie && (
                <Button
                  onClick={handleRefreshBalance}
                  disabled={refreshingBalance}
                  variant="ghost"
                  className="text-blue-400 hover:text-blue-300"
                >
                  {refreshingBalance ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    <RefreshCw size={18} />
                  )}
                  <span className="ml-2">Refresh</span>
                </Button>
              )}
            </div>
          </div>

          {user.roblox_cookie && (
            <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/20 mb-6">
              <div className="flex items-start gap-3">
                <Key className="text-green-400 mt-0.5" size={20} />
                <div>
                  <p className="text-green-400 font-medium">Cookie Linked</p>
                  <p className="text-slate-400 text-sm mt-1">
                    P2P transfers and balance sync enabled. You can supply Robux to withdrawers.
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-4">
            <Button
              onClick={handleUnlink}
              disabled={loading}
              className="flex-1 bg-red-600 hover:bg-red-500 text-white"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : 'Unlink Account'}
            </Button>
            <Button
              onClick={() => navigate('/app/deposit')}
              className="flex-1 btn-primary"
            >
              Manage Robux
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="glass rounded-2xl p-8">
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-full bg-blue-600/20 flex items-center justify-center">
            <LinkIcon className="text-blue-400" size={32} />
          </div>
        </div>

        <h1 className="font-display text-2xl font-bold text-white text-center mb-2">
          Link Your Roblox Account
        </h1>
        <p className="text-slate-400 text-center mb-8">
          Connect your Roblox account to use chat and enable withdrawals
        </p>

        {/* Link Method Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setLinkMethod('username')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
              linkMethod === 'username'
                ? 'bg-blue-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <User size={18} />
            By Username
          </button>
          <button
            onClick={() => setLinkMethod('cookie')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
              linkMethod === 'cookie'
                ? 'bg-purple-600 text-white'
                : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
            }`}
          >
            <Key size={18} />
            With Cookie
          </button>
        </div>

        {/* Username Method */}
        {linkMethod === 'username' && (
          <>
            <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20 mb-6">
              <div className="flex items-start gap-3">
                <User className="text-blue-400 mt-0.5" size={20} />
                <div>
                  <p className="text-blue-400 font-medium">Simple Linking</p>
                  <p className="text-slate-400 text-sm mt-1">
                    Just enter your Roblox username. Your avatar will be fetched automatically.
                    This enables chat but not P2P transfers.
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={handleLinkByUsername} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-slate-300">Roblox Username</Label>
                <Input
                  id="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter your Roblox username"
                  className="input-default"
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full btn-primary"
                disabled={loading || !username.trim()}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Looking up...
                  </>
                ) : (
                  <>
                    <LinkIcon className="mr-2 h-4 w-4" />
                    Link by Username
                  </>
                )}
              </Button>
            </form>
          </>
        )}

        {/* Cookie Method */}
        {linkMethod === 'cookie' && (
          <>
            <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/20 mb-6">
              <div className="flex items-start gap-3">
                <Key className="text-purple-400 mt-0.5" size={20} />
                <div>
                  <p className="text-purple-400 font-medium">Full Access Linking</p>
                  <p className="text-slate-400 text-sm mt-1">
                    Link with your cookie to enable P2P Robux transfers and become a supplier.
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20 mb-6">
              <div className="flex items-start gap-3">
                <Shield className="text-blue-400 mt-0.5" size={20} />
                <div>
                  <p className="text-blue-400 font-medium">How to get your cookie</p>
                  <ol className="text-slate-400 text-sm mt-2 space-y-1 list-decimal list-inside">
                    <li>Open Roblox.com in your browser</li>
                    <li>Press F12 to open Developer Tools</li>
                    <li>Go to Application → Cookies → roblox.com</li>
                    <li>Find .ROBLOSECURITY and copy the entire value</li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/20 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="text-yellow-400 mt-0.5" size={20} />
                <div>
                  <p className="text-yellow-400 font-medium">Security Notice</p>
                  <p className="text-slate-400 text-sm mt-1">
                    Your cookie is stored securely and only used for P2P transfers. 
                    Never share your cookie with anyone else.
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={handleLinkByCookie} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="cookie" className="text-slate-300">.ROBLOSECURITY Cookie</Label>
                <Textarea
                  id="cookie"
                  value={cookie}
                  onChange={(e) => setCookie(e.target.value)}
                  placeholder="Paste your .ROBLOSECURITY cookie here..."
                  className="input-default min-h-[120px] font-mono text-sm"
                  required
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-purple-600 hover:bg-purple-500"
                disabled={loading || !cookie}
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Linking...
                  </>
                ) : (
                  <>
                    <Key className="mr-2 h-4 w-4" />
                    Link with Cookie
                  </>
                )}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
